import { createBrowserRouter, redirect, RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useAuthStore, AdminType } from '@/store/useAuthStore';
import { useTeamStore } from '@/store/useTeamStore';
import { userRoutes } from './client';
import { adminRoutes } from './admin';
import LoadingSpinner from '@/components/LoadingSpinner';

// 页面组件导入
import Login from '@/pages/account/Login';
import Register from '@/pages/account/Register';
import NotFound from '@/pages/NotFound';

// 懒加载重组件
const ProblemSubmit = lazy(() => import('@/pages/share/problem/Submit'));
const PaperEditor = lazy(() => import('@/pages/admin/question/PaperEditor'));
const PaperSubmissions = lazy(() => import('@/pages/admin/question/PaperSubmissions'));
// 管理员专属全屏白板（独立页面，不使用布局）
const AdminWhiteboard = lazy(() => import('@/pages/admin/whiteboard'));



// 自定义路由类型（导出供其他路由文件使用）
export type MyRouteObject = RouteObject & {
  breadcrumb?: string;
  children?: MyRouteObject[];
};

// 权限检查函数（导出供其他路由文件使用）
export const checkAuth = () => {
  return useAuthStore.getState().isAuthenticated;
};

export const checkAdminAuth = () => {
  const auth = useAuthStore.getState();
  
  // Check if user is authenticated and has admin privileges
  if (!auth.isAuthenticated || !auth.user) {
    return false;
  }
  
  // Check admin_type - anyone not "Regular User" is considered an admin
  const { admin_type } = auth.user;
  return admin_type !== "Regular User";
};

/**
 * 超级管理员校验。
 *
 * 「网站配置」「评测服务器」是站点级配置（原「系统管理 → 系统设置」的两个 Tab），
 * 前端只放行超管入口；真正的权限边界在后端。
 */
export const checkSuperAdminAuth = () => {
  const auth = useAuthStore.getState();
  return !!auth.isAuthenticated && auth.user?.admin_type === AdminType.SUPER_ADMIN;
};

/**
 * 能否维护训练（训练管理入口的放行条件）：管理员，或「带教学班的老师」。
 *
 * 老师 = 团队创建者（team.creator），系统里仍是普通用户（admin_type = Regular User），
 * 因此不能只靠 admin_type 判断。团队数据来自 `/api/account/teams/my_team/`
 * （已含自建团队）：store 为空时先拉一次，避免路由守卫在数据未就绪时误判。
 * 真正的权限边界在后端（跨团队写入一律 403），前端只负责放行入口。
 */
// 团队数据就绪的共享 promise：父子 loader 会并发执行，必须等同一份数据，
// 否则子 loader 会在 myTeams 还空时误判「不是老师」并把用户踢回用户端。
let teamReadyPromise: Promise<void> | null = null;

const ensureMyTeamsLoaded = (): Promise<void> => {
  const teamStore = useTeamStore.getState();
  if (teamStore.myTeams.length > 0) return Promise.resolve();
  if (!teamReadyPromise) {
    teamReadyPromise = teamStore
      .fetchMyTeam()
      .catch(() => undefined)
      .finally(() => {
        teamReadyPromise = null;
      });
  }
  return teamReadyPromise;
};

export const canManageTraining = async (): Promise<boolean> => {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated || !auth.user) return false;
  if (auth.user.admin_type !== 'Regular User') return true;

  await ensureMyTeamsLoaded();
  const latest = useTeamStore.getState();
  return latest.myTeams.some((t) => t.creator === auth.user?.id);
};

// 训练管理入口守卫：管理员或带团队（教学班）的老师
export const checkTrainingAuth = async (): Promise<boolean> => {
  if (!checkAuth()) return false;
  return canManageTraining();
};

// 通用公共路由（登录、注册等）
const publicRoutes: MyRouteObject[] = [
  {
    path: '/login',
    element: <Login />,
    breadcrumb: '登录',
    loader: () => checkAuth() ? redirect('/') : null,
  },
  {
    path: '/register',
    element: <Register />,
    breadcrumb: '注册',
    loader: () => checkAuth() ? redirect('/') : null,
  },
  {
    path: '/logout',
    loader: () => {
      useAuthStore.getState().clearAuth();
      return redirect('/login');
    },
  },
  {
    path: '/problem/:id/submit',
    element: (
      <Suspense fallback={<LoadingSpinner tip="加载代码编辑器..." />}>
        <ProblemSubmit />
      </Suspense>
    ),
    breadcrumb: '提交代码',
    loader: () => checkAuth() ? null : redirect('/login'),
  },
  // 试卷编辑器（独立页面，不使用布局）
  {
    path: '/admin/question/paper/create',
    element: (
      <Suspense fallback={<LoadingSpinner tip="加载编辑器..." />}>
        <PaperEditor />
      </Suspense>
    ),
    breadcrumb: '创建试卷',
    loader: () => checkAdminAuth() ? null : redirect('/login'),
  },
  {
    path: '/admin/question/paper/edit/:id',
    element: (
      <Suspense fallback={<LoadingSpinner tip="加载编辑器..." />}>
        <PaperEditor />
      </Suspense>
    ),
    breadcrumb: '编辑试卷',
    loader: () => checkAdminAuth() ? null : redirect('/login'),
  },
  // 试卷提交管理（独立页面，不使用布局，与试卷编辑器保持一致）
  {
    path: '/admin/question/paper/submissions/:id',
    element: (
      <Suspense fallback={<LoadingSpinner tip="加载中..." />}>
        <PaperSubmissions />
      </Suspense>
    ),
    breadcrumb: '提交管理',
    loader: () => checkAdminAuth() ? null : redirect('/login'),
  },
  // 管理员专属全屏白板（独立页面，与试卷提交管理同样不使用布局）
  {
    path: '/admin/whiteboard',
    element: (
      <Suspense fallback={<LoadingSpinner tip="加载中..." />}>
        <AdminWhiteboard />
      </Suspense>
    ),
    breadcrumb: '白板',
    loader: () => checkAdminAuth() ? null : redirect('/login'),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

// 合并所有路由
const routes: MyRouteObject[] = [
  ...publicRoutes,
  ...adminRoutes,
  ...userRoutes,
];

// 创建路由
const router = createBrowserRouter(routes);

export { routes };  // 导出路由配置以供包屑使用
export default router; 